// src/features/team/TeamSectionNav.tsx
// Sibling navigation between a team's sub-pages, so no section is a dead end.
// Two groups mirror the hub tabs they hang off: "people" (governance pages
// behind the People tab) and "settings" (configuration pages behind the
// Settings tab). Rendered under the breadcrumb on every sub-page.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { teamApi } from '@core/api';
import type { TeamUserPermissions } from '@core/types/team/TeamUserPermissions';

export type TeamSectionGroup = 'people' | 'settings';

interface SectionItem {
  key: string;
  path: string;
  labelKey: string;
}

const PEOPLE_SECTIONS: SectionItem[] = [
  { key: 'members', path: 'members', labelKey: 'team.nav.members' },
  { key: 'roles', path: 'roles', labelKey: 'team.nav.roles' },
  { key: 'applications', path: 'applications', labelKey: 'team.nav.applications' },
  { key: 'schedules', path: 'schedules', labelKey: 'team.nav.schedules' },
  { key: 'delegations', path: 'delegations', labelKey: 'team.nav.delegations' },
  { key: 'temporary-roles', path: 'temporary-roles', labelKey: 'team.nav.temporaryRoles' },
  { key: 'ownership', path: 'ownership', labelKey: 'team.nav.ownership' },
  { key: 'audit', path: 'audit', labelKey: 'team.nav.audit' },
  { key: 'automation-log', path: 'automation-log', labelKey: 'team.nav.automationLog' },
];

const SETTINGS_SECTIONS: SectionItem[] = [
  { key: 'settings', path: 'settings', labelKey: 'team.nav.settings' },
  { key: 'branding', path: 'branding', labelKey: 'team.nav.branding' },
  { key: 'my-settings', path: 'my-settings', labelKey: 'team.nav.mySettings' },
];

interface TeamSectionNavProps {
  teamId: string;
  group: TeamSectionGroup;
  /** `key` of the section this page renders. */
  active: string;
}

export function TeamSectionNav({ teamId, group, active }: TeamSectionNavProps) {
  const { t } = useI18n();
  const [permissions, setPermissions] = useState<TeamUserPermissions | null>(null);

  useEffect(() => {
    if (!teamId) return;
    let alive = true;
    setPermissions(null);
    teamApi.GetTeamDetails(teamId)
      .then((res) => { if (alive) setPermissions(res.permissions ?? null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [teamId]);

  const sections = (group === 'people' ? PEOPLE_SECTIONS : SETTINGS_SECTIONS).filter((section) => {
    if (section.key === 'my-settings') return true;
    if (group === 'settings') return Boolean(permissions?.can_manage_settings);
    if (section.key === 'members' || section.key === 'roles') return Boolean(permissions?.can_view);
    if (section.key === 'ownership') return Boolean(permissions?.can_transfer_ownership);
    return Boolean(permissions?.can_manage_settings);
  });

  return (
    <nav aria-label={t('team.sectionNav.label')} className="-mx-1 overflow-x-auto no-scrollbar">
      <div className="flex items-center gap-1 px-1">
        {sections.map((s) => {
          const isActive = s.key === active;
          return (
            <Link
              key={s.key}
              to={`/teams/${teamId}/${s.path}`}
              aria-current={isActive ? 'page' : undefined}
              className={`whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
              }`}
            >
              {t(s.labelKey)}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
